import type { SiteBuilderBlock } from "./siteBuilderTypes";

const SITE_BUILDER_COLUMN_INLINE_BLOCK_TYPES = new Set([
    "heading",
    "link",
    "paragraph",
    "text",
]);

/**
 * Plain textual children are edited inline while their parent Column owns the
 * visual interaction frame. Rich components such as Quote, List and Button
 * intentionally keep their own frame.
 */
export function siteBuilderBlockDelegatesOutlineToColumn(
    block: Pick<SiteBuilderBlock, "type"> | string | null | undefined,
): boolean {
    const type = typeof block === "string" ? block : block?.type;

    return type !== undefined && SITE_BUILDER_COLUMN_INLINE_BLOCK_TYPES.has(type);
}

export const SITE_BUILDER_EDITOR_ATTRIBUTES = {
    accordionPart: "data-builder-accordion-part",
    accordionTitle: "data-builder-accordion-title",
    accordionToggle: "data-builder-accordion-toggle",
    blockId: "data-builder-block-id",
    blockPath: "data-builder-block-path",
    blockType: "data-builder-block-type",
    blockUuid: "data-builder-block-uuid",
    columnPart: "data-builder-column-part",
    columnRoot: "data-builder-column-root",
    emptyTextPlaceholder: "data-builder-empty-text-placeholder",
    gridColumns: "data-builder-grid-cols",
    gridContainer: "data-builder-grid-container",
    gridRows: "data-builder-grid-rows",
    iconListIcon: "data-builder-icon-list-icon",
    iconListRow: "data-builder-icon-list-row",
    iconListText: "data-builder-icon-list-text",
    iconPart: "data-builder-icon-part",
    inlineTextPart: "data-builder-inline-text-part",
    lockedColumnChild: "data-builder-locked-column-child",
    lockedColumnParentPart: "data-builder-locked-column-parent-part",
    outlineDisabled: "data-builder-outline-disabled",
    outlineOwner: "data-builder-outline-owner",
    part: "data-builder-part",
    placeholder: "data-builder-placeholder",
    shareIconPart: "data-builder-share-icon-part",
    slot: "data-builder-slot",
    slotContainer: "data-builder-slot-container",
    slotItemCount: "data-builder-slot-item-count",
    socialLinkPart: "data-builder-social-link-part",
} as const;

export const SITE_BUILDER_EDITOR_SELECTORS = {
    block: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.blockPath}]`,
    column: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.columnRoot}]`,
    grid: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.gridContainer}]`,
    inlineText: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.inlineTextPart}]`,
    part: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.part}]`,
    slot: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.slot}]`,
    slotContainer: `[${SITE_BUILDER_EDITOR_ATTRIBUTES.slotContainer}]`,
} as const;

export type SiteBuilderEditorBlockAttributes = {
    "data-builder-block-id"?: string;
    "data-builder-block-path"?: string;
    "data-builder-block-type"?: string;
    "data-builder-block-uuid"?: string;
    "data-builder-part"?: string;
    "data-builder-slot"?: string;
};

export type SiteBuilderEditorPartAttributes = {
    "data-builder-inline-text-part"?: string;
    "data-builder-part"?: string;
    "data-builder-slot"?: string;
};

export type SiteBuilderEditorSlotAttributes = SiteBuilderEditorPartAttributes & {
    "data-builder-grid-cols"?: number;
    "data-builder-grid-container"?: string;
    "data-builder-grid-rows"?: number;
    "data-builder-slot-container"?: string;
    "data-builder-slot-item-count"?: number;
};

export type SiteBuilderEditorColumnAttributes = {
    "data-builder-column-part"?: string;
    "data-builder-column-root"?: "true";
};

export type SiteBuilderEditorNestedBlockAttributes = {
    "data-builder-locked-column-child"?: "true";
    "data-builder-locked-column-parent-part"?: string;
    "data-builder-outline-disabled"?: "true";
};

export type SiteBuilderEditorOutlineOwnerAttributes = {
    "data-builder-outline-owner"?: "true";
};

export type SiteBuilderEditorInlinePlaceholderAttributes = {
    "data-builder-empty-text-placeholder"?: "true";
    "data-builder-placeholder"?: string;
};

export type SiteBuilderEditorSelection = {
    blockId: string;
    blockPath: string;
    blockType: string;
    blockUuid: string | null;
    inlineTextPart: string | null;
    part: string;
    slot: string | null;
};

export type SiteBuilderEditorElement = {
    closest(selectors: string): SiteBuilderEditorElement | null;
    getAttribute(qualifiedName: string): string | null;
};

function pathSegment(block: SiteBuilderBlock, index: number): string {
    const uuid = block.uuid?.trim();

    return uuid
        ? `uuid:${encodeURIComponent(uuid)}`
        : `index:${index}:${encodeURIComponent(block.type)}`;
}

export function siteBuilderEditorRootPath(
    block: SiteBuilderBlock,
    index: number,
): string {
    return `/blocks/${pathSegment(block, index)}`;
}

export function siteBuilderEditorChildPath(
    parentPath: string,
    slot: string,
    block: SiteBuilderBlock,
    index: number,
): string {
    return slot === "children"
        ? `${parentPath}/children/${pathSegment(block, index)}`
        : `${parentPath}/slots/${encodeURIComponent(slot)}/${pathSegment(block, index)}`;
}

export function siteBuilderEditorBlockAttributes({
    block,
    editorMode,
    path,
    slot,
}: {
    block: SiteBuilderBlock;
    editorMode: boolean;
    path: string;
    slot?: string;
}): SiteBuilderEditorBlockAttributes {
    if (!editorMode) {
        return {};
    }

    const uuid = block.uuid?.trim();

    return {
        "data-builder-block-id": uuid || path,
        "data-builder-block-path": path,
        "data-builder-block-type": block.type,
        "data-builder-block-uuid": uuid || undefined,
        "data-builder-part": "root",
        "data-builder-slot": slot,
    };
}

export function siteBuilderEditorPartAttributes({
    editorMode,
    inlineText = false,
    part,
    slot,
}: {
    editorMode: boolean;
    inlineText?: boolean;
    part: string;
    slot?: string;
}): SiteBuilderEditorPartAttributes {
    if (!editorMode) {
        return {};
    }

    return {
        "data-builder-inline-text-part": inlineText ? part : undefined,
        "data-builder-part": part,
        "data-builder-slot": slot,
    };
}

export function siteBuilderEditorSlotAttributes({
    editorMode,
    gridColumns,
    gridRows,
    itemCount,
    slot,
}: {
    editorMode: boolean;
    gridColumns?: number;
    gridRows?: number;
    itemCount?: number;
    slot: string;
}): SiteBuilderEditorSlotAttributes {
    if (!editorMode) {
        return {};
    }

    return {
        "data-builder-part": `slot:${slot}`,
        "data-builder-slot": slot,
        "data-builder-slot-container": slot,
        ...(itemCount !== undefined ? {
            "data-builder-slot-item-count": itemCount,
        } : {}),
        ...(gridColumns !== undefined || gridRows !== undefined ? {
            "data-builder-grid-cols": gridColumns,
            "data-builder-grid-container": slot,
            "data-builder-grid-rows": gridRows,
        } : {}),
    };
}

export function siteBuilderEditorColumnAttributes({
    editorMode,
    part,
}: {
    editorMode: boolean;
    part: string;
}): SiteBuilderEditorColumnAttributes {
    return editorMode ? {
        "data-builder-column-part": part,
        "data-builder-column-root": "true",
    } : {};
}

export function siteBuilderEditorNestedBlockAttributes({
    block,
    editorMode,
    lockedColumnParentPart,
}: {
    block: SiteBuilderBlock;
    editorMode: boolean;
    lockedColumnParentPart?: string;
}): SiteBuilderEditorNestedBlockAttributes {
    if (!editorMode) {
        return {};
    }

    const lockedColumnChild = Boolean(lockedColumnParentPart);

    return {
        "data-builder-locked-column-child": lockedColumnChild ? "true" : undefined,
        "data-builder-locked-column-parent-part": lockedColumnParentPart,
        "data-builder-outline-disabled": siteBuilderBlockDelegatesOutlineToColumn(block)
            ? "true"
            : undefined,
    };
}

export function siteBuilderEditorOutlineOwnerAttributes(
    editorMode: boolean,
): SiteBuilderEditorOutlineOwnerAttributes {
    return editorMode ? {
        "data-builder-outline-owner": "true",
    } : {};
}

export function siteBuilderEditorInlinePlaceholderAttributes({
    block,
    editorMode,
    empty,
}: {
    block: SiteBuilderBlock;
    editorMode: boolean;
    empty: boolean;
}): SiteBuilderEditorInlinePlaceholderAttributes {
    const commandLine = block.options?.editorCommandLine === true
        || block.options?.editorCommandLine === "true";

    return editorMode && empty && commandLine ? {
        "data-builder-empty-text-placeholder": "true",
        "data-builder-placeholder": "Type \"/\" for commands",
    } : {};
}

function closestWithinBlock(
    target: SiteBuilderEditorElement,
    selector: string,
    block: SiteBuilderEditorElement,
): SiteBuilderEditorElement | null {
    const element = target.closest(selector);

    return element?.closest(SITE_BUILDER_EDITOR_SELECTORS.block) === block
        ? element
        : null;
}

export function siteBuilderEditorSelectionFromElement(
    target: SiteBuilderEditorElement | null,
): SiteBuilderEditorSelection | null {
    if (!target) {
        return null;
    }

    const block = target.closest(SITE_BUILDER_EDITOR_SELECTORS.block);

    if (!block) {
        return null;
    }

    const blockPath = block.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.blockPath);
    const blockType = block.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.blockType);

    if (!blockPath || !blockType) {
        return null;
    }

    const part = closestWithinBlock(
        target,
        SITE_BUILDER_EDITOR_SELECTORS.part,
        block,
    );
    const slot = closestWithinBlock(
        target,
        SITE_BUILDER_EDITOR_SELECTORS.slot,
        block,
    );
    const inlineText = closestWithinBlock(
        target,
        SITE_BUILDER_EDITOR_SELECTORS.inlineText,
        block,
    );
    const blockUuid = block.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.blockUuid);

    return {
        blockId: block.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.blockId)
            || blockUuid
            || blockPath,
        blockPath,
        blockType,
        blockUuid,
        inlineTextPart: inlineText?.getAttribute(
            SITE_BUILDER_EDITOR_ATTRIBUTES.inlineTextPart,
        ) ?? null,
        part: part?.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.part) ?? "root",
        slot: slot?.getAttribute(SITE_BUILDER_EDITOR_ATTRIBUTES.slot) ?? null,
    };
}
